import React from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import AsyncSelect from "react-select/async";
import api from "../../../../api/api";
import "bootstrap-daterangepicker/daterangepicker.css";

const DeepSeekSidebar = ({
    users,
    schools = [],
    filters = {},
    onApplyFilters,
    selectedUser,
    sidebarSearch,
    setSidebarSearch,
    onSelectUser,
    onClearSelected,
    sidebarOpen,
    setSidebarOpen,
    t
}) => {


    const [showAdvSearch, setShowAdvSearch] = React.useState(false);
    const [selectedSchoolOpt, setSelectedSchoolOpt] = React.useState(null);
    const [localFilters, setLocalFilters] = React.useState({
        dateFrom: filters.dateFrom || "",
        dateTo: filters.dateTo || "",
        schoolId: filters.schoolId || "",
        userTypeId: filters.userTypeId || "",
        userName: filters.userName || "",
        pageSize: filters.pageSize || 100
    });

    React.useEffect(() => {
        setLocalFilters({
            dateFrom: filters.dateFrom || "",
            dateTo: filters.dateTo || "",
            schoolId: filters.schoolId || "",
            userTypeId: filters.userTypeId || "",
            userName: filters.userName || "",
            pageSize: filters.pageSize || 100
        });

        // Set the correct option for react-select value
        if (filters.schoolId) {
            const found = schools.find(s => s.Id === filters.schoolId);
            if (found) {
                setSelectedSchoolOpt({ value: found.Id, label: found.Name });
            } else {
                setSelectedSchoolOpt({ value: filters.schoolId, label: filters.schoolName || "" });
            }
        } else {
            setSelectedSchoolOpt(null);
        }
    }, [filters, schools]);

    const handleFilterChange = (field, val) => {
        setLocalFilters(prev => ({
            ...prev,
            [field]: val
        }));
    };

    const handleSchoolChange = (opt) => {
        setSelectedSchoolOpt(opt);
        handleFilterChange('schoolId', opt ? opt.value : "");
    };

    const promiseSchoolOptions = React.useCallback((inputValue, callback) => {
        api.post(`/SchoolsLookups/`, { Name: inputValue })
            .then((resp) => {
                if (resp.data.data?.length > 0) {
                    callback(resp.data.data.map(({ Id, Name }) => ({ label: Name, value: Id })));
                } else {
                    callback([]);
                }
            })
            .catch(() => callback([]));
    }, []);

    const handleApplyDate = (event, picker) => {
        setLocalFilters(prev => {
            const nextFilters = {
                ...prev,
                dateFrom: picker.startDate.format('YYYY-MM-DD'),
                dateTo: picker.endDate.format('YYYY-MM-DD')
            };
            // Instantly apply date filter on date range selection, like SMS
            onApplyFilters(nextFilters);
            return nextFilters;
        });
    };

    const dateRangeLabel = localFilters.dateFrom && localFilters.dateTo 
        ? `${moment(localFilters.dateFrom).format('DD/MM/YYYY')} - ${moment(localFilters.dateTo).format('DD/MM/YYYY')}` 
        : t("Pages.DeepSeekAdmin.selectDateRange") || "اختر الفترة الزمنية";

    const handleSearch = (e) => {
        e.preventDefault();
        onApplyFilters(localFilters);
    };

    const handleReset = () => {
        const resetObj = {
            dateFrom: "",
            dateTo: "",
            schoolId: "",
            userTypeId: "",
            userName: "",
            pageSize: 100
        };
        setSelectedSchoolOpt(null);
        setLocalFilters(resetObj);
        onApplyFilters(resetObj);
    };

    // Filter sidebar users list locally in case user types in standard search box
    const filteredUsers = users.filter(u =>
        u.Name.toLowerCase().includes(sidebarSearch.toLowerCase()) ||
        (u.Username && u.Username.toLowerCase().includes(sidebarSearch.toLowerCase()))
    );

    return (
        <div className={`deepseek-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
            <div className="deepseek-sidebar-header">
                <h4>{t('Pages.DeepSeekAdmin.title') || "إدارة ديب سيك والذكاء الاصطناعي"}</h4>
                <div className="d-flex align-items-center gap-2">
                    <button
                        className={`btn btn-sm btn-outline-primary d-none d-md-inline-block ${!selectedUser ? 'active' : ''}`}
                        onClick={onClearSelected}
                        title={t('Pages.DeepSeekAdmin.allStats') || "الإحصائيات العامة"}
                    >
                        <i className="fal fa-chart-line"></i>
                    </button>
                    <button
                        className="btn btn-sm btn-outline-secondary d-md-none"
                        onClick={() => setSidebarOpen(false)}
                        title={t('close') || "إغلاق"}
                    >
                        <i className="fal fa-times"></i>
                    </button>
                </div>
            </div>

            {/* Search and Filters styled like SMS */}
            <div className="deepseek-search-container p-3 border-bottom">
                {/* Search input */}
                <div className="deepseek-search-wrapper mb-3 position-relative" style={{ backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '4px 12px' }}>
                    <i className="fal fa-search deepseek-search-icon" style={{ color: '#94a3b8' }}></i>
                    <input
                        type="text"
                        className="deepseek-search-input"
                        placeholder={t('SearchName.Placeholder') || "بحث عن مستخدم..."}
                        value={sidebarSearch}
                        onChange={(e) => setSidebarSearch(e.target.value)}
                        style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', padding: '6px' }}
                    />
                </div>

                {/* Date Picker (always visible) */}
                <div className="row g-2 mb-2 text-right">
                    <div className="col-12">
                        <DateRangePicker
                            initialSettings={{
                                opens: 'left',
                                autoUpdateInput: true,
                                startDate: localFilters.dateFrom ? moment(localFilters.dateFrom).toDate() : moment().subtract(29, 'days').toDate(),
                                endDate: localFilters.dateTo ? moment(localFilters.dateTo).toDate() : moment().toDate(),
                                locale: {
                                    format: 'DD/MM/YYYY',
                                    applyLabel: t('Apply') || 'اعتماد',
                                    cancelLabel: t('Close') || 'إغلاق',
                                    customRangeLabel: t('range.customRange') || 'فترة مخصصة'
                                }
                            }}
                            onApply={handleApplyDate}
                        >
                            <input
                                type="text"
                                className="deepseek-date-input form-control form-control-sm text-center clickable"
                                readOnly
                                value={dateRangeLabel}
                                style={{ cursor: 'pointer', borderRadius: '6px' }}
                            />
                        </DateRangePicker>
                    </div>
                </div>

                {/* More Filters Toggle */}
                <div className="text-center my-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-primary text-decoration-none font-weight-bold d-flex align-items-center justify-content-center w-100 gap-1"
                        onClick={() => setShowAdvSearch(!showAdvSearch)}
                        style={{ fontSize: '12px', border: 'none', background: 'transparent', outline: 'none', color: '#92278f' }}
                    >
                        <i className={`fal ${showAdvSearch ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                        <span>{showAdvSearch ? t("Pages.DeepSeekAdmin.lessFilters") || "إخفاء خيارات التصفية" : t("Pages.DeepSeekAdmin.moreFilters") || "خيارات تصفية إضافية"}</span>
                    </button>
                </div>

                {/* Other Filters (Collapsible) */}
                {showAdvSearch && (
                    <form onSubmit={handleSearch} className="deepseek-adv-search-panel mt-2 p-3 border rounded bg-white text-right animate__animated animate__fadeIn" style={{ animationDuration: '0.2s' }}>
                        <h6 className="m-0 mb-3 pb-2 text-right border-bottom font-weight-bold" style={{ fontSize: '13px', color: '#1e293b' }}>
                            {t('Pages.DeepSeekAdmin.advSearch') || "خيارات البحث المتقدم"}
                        </h6>

                        <div className="form-group mb-2">
                            <label className="small text-muted mb-1 d-block">{t('Pages.DeepSeekAdmin.school') || "المدرسة"}</label>
                            <AsyncSelect
                                cacheOptions
                                isClearable={true}
                                placeholder={t('Pages.DeepSeekAdmin.school') || "المدرسة"}
                                isRtl={true}
                                isSearchable={true}
                                defaultOptions
                                loadOptions={promiseSchoolOptions}
                                onChange={handleSchoolChange}
                                value={selectedSchoolOpt}
                                theme={theme => ({
                                    ...theme,
                                    borderRadius: 6,
                                    colors: {
                                        ...theme.colors,
                                        primary25: '#92278f',
                                        primary: '#92278f',
                                    },
                                })}
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '31px',
                                        height: '31px',
                                        fontSize: '12px',
                                        borderRadius: '6px',
                                        borderColor: '#ced4da',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            borderColor: '#92278f'
                                        }
                                    }),
                                    valueContainer: (provided) => ({
                                        ...provided,
                                        padding: '0 8px',
                                        height: '31px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }),
                                    input: (provided) => ({
                                        ...provided,
                                        margin: '0px',
                                        padding: '0px'
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        margin: '0px'
                                    }),
                                    indicatorsContainer: (provided) => ({
                                        ...provided,
                                        height: '29px'
                                    }),
                                    dropdownIndicator: (provided) => ({
                                        ...provided,
                                        padding: '4px'
                                    }),
                                    clearIndicator: (provided) => ({
                                        ...provided,
                                        padding: '4px'
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        fontSize: '12px',
                                        padding: '6px 12px',
                                        backgroundColor: state.isSelected ? '#92278f' : (state.isFocused ? '#f1f5f9' : 'transparent'),
                                        color: state.isSelected ? '#ffffff' : '#334155',
                                        '&:active': {
                                            backgroundColor: '#92278f',
                                            color: '#ffffff'
                                        }
                                    })
                                }}
                            />
                        </div>

                        <div className="form-group mb-2">
                            <label className="small text-muted mb-1 d-block">{t('Pages.DeepSeekAdmin.userType') || "نوع المستخدم"}</label>
                            <select
                                className="form-control form-control-sm text-right px-2"
                                value={localFilters.userTypeId}
                                onChange={(e) => handleFilterChange('userTypeId', e.target.value)}
                                style={{ height: '31px', borderRadius: '6px' }}
                            >
                                <option value="">{t('Pages.DeepSeekAdmin.all') || "الكل"}</option>
                                <option value="1">{t('Pages.DeepSeekAdmin.admin') || "مدير نظام"}</option>
                                <option value="2">{t('Pages.DeepSeekAdmin.supervisor') || "مشرف"}</option>
                                <option value="3">{t('Pages.DeepSeekAdmin.schoolManager') || "مدير مدرسة"}</option>
                                <option value="4">{t('Pages.DeepSeekAdmin.teacher') || "معلم"}</option>
                                <option value="5">{t('Pages.DeepSeekAdmin.student') || "طالب"}</option>
                                <option value="6">{t('Pages.DeepSeekAdmin.parent') || "ولي أمر"}</option>
                            </select>
                        </div>

                        <div className="form-group mb-2">
                            <label className="small text-muted mb-1 d-block">{t('Pages.DeepSeekAdmin.userName') || "اسم المستخدم"}</label>
                            <input
                                type="text"
                                className="form-control form-control-sm text-right"
                                placeholder={t('Pages.DeepSeekAdmin.searchUserPlaceholder') || "اسم أو بريد المستخدم..."}
                                value={localFilters.userName}
                                onChange={(e) => handleFilterChange('userName', e.target.value)}
                                style={{ borderRadius: '6px' }}
                            />
                        </div>

                        <div className="form-group mb-3">
                            <label className="small text-muted mb-1 d-block">{t('Pages.DeepSeekAdmin.limit') || "عدد النتائج"}</label>
                            <input
                                type="number"
                                className="form-control form-control-sm text-right"
                                min="1"
                                max="1000"
                                value={localFilters.pageSize}
                                onChange={(e) => handleFilterChange('pageSize', parseInt(e.target.value) || 100)}
                                style={{ borderRadius: '6px' }}
                            />
                        </div>

                        <div className="d-flex justify-content-start gap-2 mt-3">
                            <button
                                type="submit"
                                className="btn btn-sm btn-primary ml-2 px-3"
                                style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#92278f', borderColor: '#92278f' }}
                            >
                                <i className="fal fa-search"></i>
                                <span>{t('Pages.DeepSeekAdmin.searchBtn') || "بحث"}</span>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-light px-3"
                                onClick={handleReset}
                                style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                                <i className="fal fa-redo"></i>
                                <span>{t('Pages.DeepSeekAdmin.resetBtn') || "إعادة تعيين"}</span>
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Scrollable List of users */}
            <div className="deepseek-user-list">
                {filteredUsers.length > 0 ? (
                    filteredUsers.map(u => {
                        const initials = u.Name ? u.Name.substring(0, 2) : "AI";
                        const isUserActive = selectedUser && selectedUser.Id === u.Id;
                        return (
                            <div
                                key={u.Id}
                                className={`deepseek-user-item ${isUserActive ? 'active' : ''}`}
                                onClick={() => {
                                    onSelectUser(u);
                                    if (window.innerWidth < 768) {
                                        setSidebarOpen(false); // Close sidebar on mobile after selecting
                                    }
                                }}
                            >
                                <div className="deepseek-avatar">
                                    {u.Photo ? (
                                        <img
                                            src={u.Photo}
                                            alt={u.Name}
                                            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        initials
                                    )}
                                </div>
                                <div className="deepseek-user-info-meta">
                                    <div className="deepseek-user-name">{u.Name}</div>
                                    {u.UserTypeId !== 1 && u.UserTypeId !== 2 && u.UserTypeId !== 6 && u.SchoolName && (
                                        <div className="small text-muted text-right mb-1" style={{ fontSize: '11px' }}>
                                            <i className="fal fa-school ml-1"></i> {u.SchoolName}
                                        </div>
                                    )}
                                    <div className="deepseek-user-meta-sub">
                                        <span>{u.TotalChats} {t('Pages.DeepSeekAdmin.chats') || "محادثات"}</span>
                                        <span className="deepseek-spent-badge">
                                            ${parseFloat(u.TotalSpent).toFixed(4)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-4 text-muted small">
                        {t('NoInfo') || "لا يوجد مستخدمين حالياً"}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeepSeekSidebar;
